import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { profileAPI } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'
import {
  XMarkIcon,
  ScaleIcon,
  PlusIcon,
  PhotoIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

export default function BodyMeasurementForm({ isOpen, onClose }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const fileInputRef = useRef(null)

  const [formData, setFormData] = useState({
    weight_kg: '',
    body_fat_percentage: '',
    muscle_mass_kg: '',
    // Smart scale fields
    skeletal_muscle_percentage: '',
    protein_percentage: '',
    bmr_scale: '',
    fat_free_body_weight_kg: '',
    subcutaneous_fat_percentage: '',
    visceral_fat_level: '',
    body_water_percentage: '',
    bone_mass_kg: '',
    metabolic_age: '',
    // Manual measurements
    waist_cm: '',
    hip_cm: '',
    chest_cm: '',
    notes: '',
    source: 'manual',
    // Always opens at the current time. It used to default to blank, which the
    // server read as "now" — but any value left over from a screenshot analysis
    // then persisted and was easy to miss.
    measured_at: new Date().toISOString()
  })
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisError, setAnalysisError] = useState(null)
  const [dateSource, setDateSource] = useState(null)
  const isSmartScale = formData.source === 'smart_scale'

  const createMutation = useMutation({
    mutationFn: (data) => profileAPI.createMeasurement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['measurementHistory'] })
      queryClient.invalidateQueries({ queryKey: ['latestMeasurement'] })
      queryClient.invalidateQueries({ queryKey: ['profileSummary'] })
      queryClient.invalidateQueries({ queryKey: ['healthMetrics'] })
      onClose()
      resetForm()
    }
  })

  const resetForm = () => {
    setFormData({
      weight_kg: '',
      body_fat_percentage: '',
      muscle_mass_kg: '',
      skeletal_muscle_percentage: '',
      protein_percentage: '',
      bmr_scale: '',
      fat_free_body_weight_kg: '',
      subcutaneous_fat_percentage: '',
      visceral_fat_level: '',
      body_water_percentage: '',
      bone_mass_kg: '',
      metabolic_age: '',
      waist_cm: '',
      hip_cm: '',
      chest_cm: '',
      notes: '',
      source: 'manual',
      measured_at: new Date().toISOString()
    })
    setShowAdvanced(false)
    setAnalysisError(null)
    setDateSource(null)
  }

  // A `datetime-local` input speaks LOCAL time; measured_at is stored as an ISO
  // UTC instant. Slicing the ISO string fed UTC straight into the input, so east
  // of Greenwich the clock read early — a measurement at 08:00 CEST displayed as
  // 06:00. Reported from production. These two convert rather than truncate.
  const toLocalInput = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  // CR-01: Helper function to round numeric values to 1 decimal place
  // Matches the step="0.1" on form inputs so browser validation passes
  const roundTo1Decimal = (value) => {
    if (value === null || value === undefined || value === '') return ''
    const num = parseFloat(value)
    return isNaN(num) ? '' : Math.round(num * 10) / 10
  }

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsAnalyzing(true)
    setAnalysisError(null)

    try {
      const response = await profileAPI.analyzeScaleScreenshot(file)
      const data = response.data

      // CR-01: Fill form with extracted data, rounded to 1 decimal place
      // Must match step="0.1" on inputs to pass browser validation
      setFormData(prev => ({
        ...prev,
        weight_kg: roundTo1Decimal(data.weight_kg),
        body_fat_percentage: roundTo1Decimal(data.body_fat_percentage),
        muscle_mass_kg: roundTo1Decimal(data.muscle_mass_kg),
        skeletal_muscle_percentage: roundTo1Decimal(data.skeletal_muscle_percentage),
        protein_percentage: roundTo1Decimal(data.protein_percentage),
        bmr_scale: roundTo1Decimal(data.bmr_scale),
        fat_free_body_weight_kg: roundTo1Decimal(data.fat_free_body_weight_kg),
        subcutaneous_fat_percentage: roundTo1Decimal(data.subcutaneous_fat_percentage),
        visceral_fat_level: data.visceral_fat_level || '', // Keep as integer
        body_water_percentage: roundTo1Decimal(data.body_water_percentage),
        bone_mass_kg: roundTo1Decimal(data.bone_mass_kg),
        metabolic_age: data.metabolic_age || '', // Keep as integer
        source: 'smart_scale',
        measured_at: data.measured_at || ''
      }))

      // Auto-expand to show all fields
      setShowAdvanced(true)
      setDateSource(data.date_source)

    } catch (error) {
      console.error('Screenshot analysis failed:', error)
      setAnalysisError(
        apiErrorMessage(
          error,
          t('profile.screenshotAnalysisFailed', 'Failed to analyze screenshot. Please try again or enter data manually.')
        )
      )
    } finally {
      setIsAnalyzing(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.weight_kg) return

    const data = {
      weight_kg: parseFloat(formData.weight_kg),
      source: formData.source
    }

    // Add measured_at if we have it from screenshot
    if (formData.measured_at) {
      data.measured_at = formData.measured_at
    }

    // Core fields that are columns in the database
    if (formData.body_fat_percentage) {
      data.body_fat_percentage = parseFloat(formData.body_fat_percentage)
    }
    if (formData.muscle_mass_kg) {
      data.muscle_mass_kg = parseFloat(formData.muscle_mass_kg)
    }

    // Manual measurement fields
    const manualFields = ['waist_cm', 'hip_cm', 'chest_cm']
    manualFields.forEach(field => {
      if (formData[field]) {
        data[field] = parseFloat(formData[field])
      }
    })

    // Dynamic smart scale metrics stored as additional_metrics
    const additional_metrics = {}
    const dynamicFloatFields = [
      'skeletal_muscle_percentage', 'protein_percentage', 'bmr_scale',
      'fat_free_body_weight_kg', 'subcutaneous_fat_percentage',
      'body_water_percentage', 'bone_mass_kg'
    ]
    dynamicFloatFields.forEach(field => {
      if (formData[field]) {
        additional_metrics[field] = parseFloat(formData[field])
      }
    })

    // Integer dynamic fields
    const dynamicIntFields = ['visceral_fat_level', 'metabolic_age']
    dynamicIntFields.forEach(field => {
      if (formData[field]) {
        additional_metrics[field] = parseInt(formData[field])
      }
    })

    // Only add additional_metrics if we have any
    if (Object.keys(additional_metrics).length > 0) {
      data.additional_metrics = additional_metrics
    }

    if (formData.notes) {
      data.notes = formData.notes
    }

    createMutation.mutate(data)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-surface-container rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-surface-container">
            <div className="flex items-center">
              <ScaleIcon className="h-6 w-6 text-indigo-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('profile.logMeasurement', 'Log Measurement')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Screenshot Upload */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <PhotoIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-900">
                    {t('profile.uploadScreenshot', 'Upload Scale Screenshot')}
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="screenshot-upload"
                />
                <label
                  htmlFor="screenshot-upload"
                  className={`btn-primary text-sm cursor-pointer flex items-center ${isAnalyzing ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isAnalyzing ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                      {t('common.analyzing', 'Analyzing...')}
                    </>
                  ) : (
                    <>
                      <PhotoIcon className="h-4 w-4 mr-1" />
                      {t('profile.uploadPhoto', 'Upload Photo')}
                    </>
                  )}
                </label>
              </div>
              <p className="text-xs text-blue-700 mt-2">
                {t('profile.screenshotHint', 'Upload a screenshot from your scale app (Arboleaf, Withings, etc.) to auto-fill all values')}
              </p>
              {dateSource && (
                <p className="text-xs text-green-700 mt-1">
                  {dateSource === 'exif' && t('profile.dateFromPhoto', 'Date extracted from photo metadata')}
                  {dateSource === 'screenshot' && t('profile.dateFromScreenshot', 'Date extracted from screenshot')}
                  {dateSource === 'current' && t('profile.dateUsingCurrent', 'Using current date/time')}
                </p>
              )}
            </div>

            {/* Analysis Error */}
            {analysisError && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                {analysisError}
              </div>
            )}

            {/* Weight - Required */}
            <div>
              <label className="form-label">
                {t('profile.weight', 'Weight')} (kg) *
              </label>
              <input
                type="number"
                value={formData.weight_kg}
                onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                className="form-input"
                step="0.1"
                min="20"
                max="500"
                required
              />
            </div>

            {/* Date/Time */}
            <div>
              <label className="form-label">
                {t('common.date', 'Date')} / {t('common.time', 'Time')}
              </label>
              <input
                type="datetime-local"
                value={toLocalInput(formData.measured_at)}
                onChange={(e) => setFormData({ ...formData, measured_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="form-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('profile.dateHint', 'Leave empty to use current time')}
              </p>
            </div>

            {/* Source */}
            <div>
              <label className="form-label">{t('profile.source', 'Source')}</label>
              <select
                value={formData.source}
                onChange={(e) => {
                  const newSource = e.target.value
                  setFormData({ ...formData, source: newSource })
                  // Auto-expand body composition fields for smart scale
                  if (newSource === 'smart_scale') {
                    setShowAdvanced(true)
                  }
                }}
                className="form-select"
              >
                <option value="manual">{t('profile.sources.manual', 'Manual entry')}</option>
                <option value="smart_scale">{t('profile.sources.smart_scale', 'Smart scale')}</option>
                <option value="dexa">{t('profile.sources.dexa', 'DEXA scan')}</option>
              </select>
            </div>

            {/* Toggle advanced fields - hide button for smart scale since it auto-expands */}
            {!isSmartScale && (
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
              >
                <PlusIcon className={`h-4 w-4 mr-1 transition-transform ${showAdvanced ? 'rotate-45' : ''}`} />
                {showAdvanced
                  ? t('profile.hideAdvanced', 'Hide body composition')
                  : t('profile.showAdvanced', 'Add body composition')}
              </button>
            )}

            {/* Advanced Fields - always show for smart scale */}
            {(showAdvanced || isSmartScale) && (
              <div className="space-y-4 border-t pt-4">
                {/* Body Fat */}
                <div>
                  <label className="form-label">
                    {t('profile.bodyFat', 'Body Fat')} (%)
                  </label>
                  <input
                    type="number"
                    value={formData.body_fat_percentage}
                    onChange={(e) => setFormData({ ...formData, body_fat_percentage: e.target.value })}
                    className="form-input"
                    step="0.1"
                    min="1"
                    max="70"
                  />
                </div>

                {/* Muscle Mass */}
                <div>
                  <label className="form-label">
                    {t('profile.muscleMass', 'Muscle Mass')} (kg)
                  </label>
                  <input
                    type="number"
                    value={formData.muscle_mass_kg}
                    onChange={(e) => setFormData({ ...formData, muscle_mass_kg: e.target.value })}
                    className="form-input"
                    step="0.1"
                    min="10"
                    max="200"
                  />
                </div>

                {/* Detailed body composition metrics.
                    NOT gated on source: which metrics you can record is a property
                    of the measurement, not of how it arrived. Gating these on
                    smart_scale made them unreachable for manual entry even though
                    the submit path and the backend (additional_metrics) accept them
                    from any source. */}
                <div className="space-y-4 border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700">
                    {t('profile.bodyCompositionDetail', 'Body Composition Detail')}
                  </h4>

                    {/* Row 1: Skeletal Muscle % and Protein % */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="form-label">
                          {t('profile.skeletalMuscle', 'Skeletal Muscle')} (%)
                        </label>
                        <input
                          type="number"
                          value={formData.skeletal_muscle_percentage}
                          onChange={(e) => setFormData({ ...formData, skeletal_muscle_percentage: e.target.value })}
                          className="form-input"
                          step="0.1"
                          min="10"
                          max="70"
                        />
                      </div>
                      <div>
                        <label className="form-label">
                          {t('profile.protein', 'Protein')} (%)
                        </label>
                        <input
                          type="number"
                          value={formData.protein_percentage}
                          onChange={(e) => setFormData({ ...formData, protein_percentage: e.target.value })}
                          className="form-input"
                          step="0.1"
                          min="5"
                          max="30"
                        />
                      </div>
                    </div>

                    {/* Row 2: BMR and Fat-free Body Weight */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="form-label">
                          {t('profile.bmrScale', 'BMR')} (kcal)
                        </label>
                        <input
                          type="number"
                          value={formData.bmr_scale}
                          onChange={(e) => setFormData({ ...formData, bmr_scale: e.target.value })}
                          className="form-input"
                          step="1"
                          min="500"
                          max="5000"
                        />
                      </div>
                      <div>
                        <label className="form-label">
                          {t('profile.fatFreeWeight', 'Fat-free Weight')} (kg)
                        </label>
                        <input
                          type="number"
                          value={formData.fat_free_body_weight_kg}
                          onChange={(e) => setFormData({ ...formData, fat_free_body_weight_kg: e.target.value })}
                          className="form-input"
                          step="0.1"
                          min="20"
                          max="200"
                        />
                      </div>
                    </div>

                    {/* Row 3: Subcutaneous Fat and Visceral Fat */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="form-label">
                          {t('profile.subcutaneousFat', 'Subcutaneous Fat')} (%)
                        </label>
                        <input
                          type="number"
                          value={formData.subcutaneous_fat_percentage}
                          onChange={(e) => setFormData({ ...formData, subcutaneous_fat_percentage: e.target.value })}
                          className="form-input"
                          step="0.1"
                          min="1"
                          max="50"
                        />
                      </div>
                      <div>
                        <label className="form-label">
                          {t('profile.visceralFat', 'Visceral Fat')} (level)
                        </label>
                        <input
                          type="number"
                          value={formData.visceral_fat_level}
                          onChange={(e) => setFormData({ ...formData, visceral_fat_level: e.target.value })}
                          className="form-input"
                          step="1"
                          min="1"
                          max="60"
                        />
                      </div>
                    </div>

                    {/* Row 4: Body Water and Bone Mass */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="form-label">
                          {t('profile.bodyWater', 'Body Water')} (%)
                        </label>
                        <input
                          type="number"
                          value={formData.body_water_percentage}
                          onChange={(e) => setFormData({ ...formData, body_water_percentage: e.target.value })}
                          className="form-input"
                          step="0.1"
                          min="30"
                          max="80"
                        />
                      </div>
                      <div>
                        <label className="form-label">
                          {t('profile.boneMass', 'Bone Mass')} (kg)
                        </label>
                        <input
                          type="number"
                          value={formData.bone_mass_kg}
                          onChange={(e) => setFormData({ ...formData, bone_mass_kg: e.target.value })}
                          className="form-input"
                          step="0.1"
                          min="1"
                          max="10"
                        />
                      </div>
                    </div>

                    {/* Row 5: Metabolic Age */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="form-label">
                          {t('profile.metabolicAge', 'Metabolic Age')} (years)
                        </label>
                        <input
                          type="number"
                          value={formData.metabolic_age}
                          onChange={(e) => setFormData({ ...formData, metabolic_age: e.target.value })}
                          className="form-input"
                          step="1"
                          min="10"
                          max="120"
                        />
                      </div>
                    </div>
                  </div>

                {/* Body Measurements */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="form-label">{t('profile.waist', 'Waist')} (cm)</label>
                    <input
                      type="number"
                      value={formData.waist_cm}
                      onChange={(e) => setFormData({ ...formData, waist_cm: e.target.value })}
                      className="form-input"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="form-label">{t('profile.hip', 'Hip')} (cm)</label>
                    <input
                      type="number"
                      value={formData.hip_cm}
                      onChange={(e) => setFormData({ ...formData, hip_cm: e.target.value })}
                      className="form-input"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="form-label">{t('profile.chest', 'Chest')} (cm)</label>
                    <input
                      type="number"
                      value={formData.chest_cm}
                      onChange={(e) => setFormData({ ...formData, chest_cm: e.target.value })}
                      className="form-input"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="form-label">{t('common.notes', 'Notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="form-input"
                rows={2}
                placeholder={t('profile.notesPlaceholder', 'Any additional notes...')}
              />
            </div>

            {/* Error. A FastAPI 422 returns `detail` as an array of objects, so
                it must be normalised to a string before it reaches React. */}
            {createMutation.isError && (
              <div className="text-red-600 text-sm">
                {apiErrorMessage(createMutation.error, t('common.error', 'An error occurred'))}
              </div>
            )}

            {/* Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || !formData.weight_kg}
                className="btn-primary"
              >
                {createMutation.isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
